import get from "lodash/get";
import { FormikErrors, FormikTouched, getIn } from 'formik';
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { AXNUITextInputWithErrorMessage } from '@iddataweb/idw-common-ui/containers';
import { ChangeEvent, FocusEvent, RefObject } from 'react';
import { Logger } from '@utils/logger';
import { shouldHighlightInput } from '@redux/selectors/verification-form-selectors';
import { useParamSelector } from '@hooks/use-param-selector';
import { getSSNValue } from '@utils/attribute-data-util';
import { useSelector } from 'react-redux';
import { getLanguageContent } from '@redux/selectors/language-selectors';

type SSN9SerialNumberInputProps = {
    attribute: AttributeFormState;
    touched: FormikTouched<any>;
    errors: FormikErrors<any>;
    inputType: "password" | "number";
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
    inputRef: RefObject<HTMLInputElement>;
};

export const SSN9SerialNumberInput = (props: SSN9SerialNumberInputProps) => {
    const {
        attribute,
        touched,
        errors,
        inputType,
        onBlur,
        setFieldTouched,
        setFieldValue,
        onSetSubmitValue,
        isSubmitting,
        inputRef
    } = props;

    const content = useSelector(getLanguageContent);

    const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value?.length < 5) {
            Logger.info("[SSN9SerialNumberInput] submit value: ", value);
            const { areaNumber, groupNumber } = attribute.inputData;
            const ssn9 = getSSNValue(areaNumber, groupNumber, value);
            const inputData = { ...attribute.inputData, serialNumber: value, ssn9 };
            onSetSubmitValue({ inputData });
            await setFieldValue("serialNumber", value);
            setFieldTouched("serialNumber", true, true);
        }
    };

    const fieldTouched = getIn(touched, "serialNumber");
    const fieldError = getIn(errors, "serialNumber");
    const fieldErrorMessage = fieldError ? (get(content, fieldError) || fieldError) : null;
    const fieldIsInvalid = fieldTouched && fieldError;
    const fieldShouldBeHighlighted = useParamSelector(shouldHighlightInput, attribute);

    return (
        <div>
            <AXNUITextInputWithErrorMessage
                controlId={`attr${attribute.id}-inputs-serialNumber`}
                automationId={`attr${attribute.id}-inputs-serialNumber`}
                type={inputType}
                autocomplete={"off"}
                value={attribute.inputData.serialNumber || ""}
                fieldName={"serialNumber"}
                fieldError={fieldErrorMessage}
                fieldTouched={fieldTouched}
                fieldIsInvalid={fieldIsInvalid}
                fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                onBlur={onBlur}
                setFieldTouched={setFieldTouched}
                isDisabled={attribute.isDisabled}
                isPlaceholder={attribute.isPlaceholder}
                isOptional={attribute.optional}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                title={content.validation.serialNumber.required}
                placeholder={content.form.SSNCaptureInput.serialNumberPlaceholder}
                inputRef={inputRef}
            />
        </div>
    );
};